import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Image,
  TextInput,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types/navigation';
import {
  getAllIssueReports,
  updateIssueReport,
  getIssueReportStats,
} from '../utils/issueReportStorage';
import {
  ProductIssueReportWithProduct,
  ReportStatus,
  ISSUE_TYPE_LABELS,
  STATUS_LABELS,
  PRIORITY_LABELS,
} from '../types/issueReport';

type Props = NativeStackScreenProps<RootStackParamList, 'AdminReports'>;

export default function AdminReportsScreen({ navigation }: Props) {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<ProductIssueReportWithProduct[]>([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, in_review: 0, resolved: 0, rejected: 0 });
  const [filterStatus, setFilterStatus] = useState<ReportStatus | 'all'>('pending');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [resolutionAction, setResolutionAction] = useState('');

  useEffect(() => {
    loadReports();
    loadStats();
  }, [filterStatus]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const data = await getAllIssueReports(
        filterStatus === 'all' ? undefined : filterStatus
      );
      setReports(data);
    } catch (error) {
      console.error('Error loading reports:', error);
      Alert.alert('Error', 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await getIssueReportStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleUpdateStatus = async (reportId: string, newStatus: ReportStatus) => {
    try {
      const updates: any = { status: newStatus };
      
      if (newStatus === 'resolved' || newStatus === 'rejected') {
        updates.reviewed_at = new Date().toISOString();
        if (adminNotes) updates.admin_notes = adminNotes;
        if (resolutionAction) updates.resolution_action = resolutionAction;
      }

      const result = await updateIssueReport(reportId, updates);
      
      if (result) {
        Alert.alert('Success', `Report marked as ${newStatus}`);
        setExpandedId(null);
        setAdminNotes('');
        setResolutionAction('');
        loadReports();
        loadStats();
      } else {
        Alert.alert('Error', 'Failed to update report');
      }
    } catch (error) {
      console.error('Error updating report:', error);
      Alert.alert('Error', 'Failed to update report');
    }
  };

  const renderReport = (report: ProductIssueReportWithProduct) => {
    const isExpanded = expandedId === report.id;
    const product = report.user_product;
    const brandName = product?.variant?.model?.family?.brand?.name || 'Unknown';
    const modelName = product?.variant?.model?.name || 'Unknown';

    return (
      <View key={report.id} style={styles.reportCard}>
        <TouchableOpacity
          onPress={() => setExpandedId(isExpanded ? null : report.id)}
          style={styles.reportHeader}
        >
          <View style={styles.reportHeaderLeft}>
            <Text style={styles.reportIssueType}>
              {ISSUE_TYPE_LABELS[report.issue_type]}
            </Text>
            <Text style={styles.reportProduct}>
              {brandName} {modelName}
            </Text>
            <Text style={styles.reportDate}>
              {new Date(report.created_at).toLocaleDateString()}
            </Text>
          </View>
          <View style={styles.reportHeaderRight}>
            <View style={[styles.statusBadge, getStatusColor(report.status)]}>
              <Text style={styles.statusText}>{STATUS_LABELS[report.status]}</Text>
            </View>
            <View style={[styles.priorityBadge, getPriorityColor(report.priority)]}>
              <Text style={styles.priorityText}>{PRIORITY_LABELS[report.priority]}</Text>
            </View>
          </View>
        </TouchableOpacity>

        {isExpanded && (
          <View style={styles.reportDetails}>
            {/* Issue Details */}
            <View style={styles.detailSection}>
              <Text style={styles.detailLabel}>Current Value (AI):</Text>
              <Text style={styles.detailValue}>{report.current_value || 'N/A'}</Text>
            </View>

            <View style={styles.detailSection}>
              <Text style={styles.detailLabel}>Suggested Value (User):</Text>
              <Text style={styles.detailValue}>{report.suggested_value || 'N/A'}</Text>
            </View>

            {report.description && (
              <View style={styles.detailSection}>
                <Text style={styles.detailLabel}>Description:</Text>
                <Text style={styles.detailValue}>{report.description}</Text>
              </View>
            )}

            {/* Product Info */}
            <View style={styles.detailSection}>
              <Text style={styles.detailLabel}>Product ID:</Text>
              <Text style={styles.detailValue}>{report.user_product_id}</Text>
            </View>

            {/* Admin Actions */}
            {report.status === 'pending' || report.status === 'in_review' ? (
              <View style={styles.adminActions}>
                <Text style={styles.adminLabel}>Admin Notes:</Text>
                <TextInput
                  style={styles.adminInput}
                  value={adminNotes}
                  onChangeText={setAdminNotes}
                  placeholder="Add notes about this issue..."
                  placeholderTextColor="#999"
                  multiline
                  numberOfLines={3}
                  textAlignVertical="top"
                />

                <Text style={styles.adminLabel}>Resolution Action:</Text>
                <TextInput
                  style={styles.adminInput}
                  value={resolutionAction}
                  onChangeText={setResolutionAction}
                  placeholder="What action was taken?"
                  placeholderTextColor="#999"
                />

                <View style={styles.actionButtons}>
                  {report.status === 'pending' && (
                    <TouchableOpacity
                      style={[styles.actionButton, styles.reviewButton]}
                      onPress={() => handleUpdateStatus(report.id, 'in_review')}
                    >
                      <Text style={styles.actionButtonText}>Start Review</Text>
                    </TouchableOpacity>
                  )}
                  
                  <TouchableOpacity
                    style={[styles.actionButton, styles.resolveButton]}
                    onPress={() => handleUpdateStatus(report.id, 'resolved')}
                  >
                    <Text style={styles.actionButtonText}>Resolve</Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity
                    style={[styles.actionButton, styles.rejectButton]}
                    onPress={() => handleUpdateStatus(report.id, 'rejected')}
                  >
                    <Text style={styles.actionButtonText}>Reject</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <View style={styles.resolvedSection}>
                {report.admin_notes && (
                  <View style={styles.detailSection}>
                    <Text style={styles.detailLabel}>Admin Notes:</Text>
                    <Text style={styles.detailValue}>{report.admin_notes}</Text>
                  </View>
                )}
                {report.resolution_action && (
                  <View style={styles.detailSection}>
                    <Text style={styles.detailLabel}>Resolution:</Text>
                    <Text style={styles.detailValue}>{report.resolution_action}</Text>
                  </View>
                )}
                {report.reviewed_at && (
                  <View style={styles.detailSection}>
                    <Text style={styles.detailLabel}>Reviewed:</Text>
                    <Text style={styles.detailValue}>
                      {new Date(report.reviewed_at).toLocaleString()}
                    </Text>
                  </View>
                )}
              </View>
            )}
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Stats Header */}
      <View style={styles.statsContainer}>
        <Text style={styles.statsTitle}>Issue Reports</Text>
        <View style={styles.statsGrid}>
          <View style={styles.statBox}>
            <Text style={styles.statNumber}>{stats.total}</Text>
            <Text style={styles.statLabel}>Total</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={[styles.statNumber, { color: '#ff6b00' }]}>{stats.pending}</Text>
            <Text style={styles.statLabel}>Pending</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={[styles.statNumber, { color: '#0066ff' }]}>{stats.in_review}</Text>
            <Text style={styles.statLabel}>In Review</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={[styles.statNumber, { color: '#00cc00' }]}>{stats.resolved}</Text>
            <Text style={styles.statLabel}>Resolved</Text>
          </View>
        </View>
      </View>

      {/* Filter Tabs */}
      <View style={styles.filterTabs}>
        {(['all', 'pending', 'in_review', 'resolved', 'rejected'] as const).map((status) => (
          <TouchableOpacity
            key={status}
            style={[
              styles.filterTab,
              filterStatus === status && styles.filterTabActive,
            ]}
            onPress={() => setFilterStatus(status)}
          >
            <Text
              style={[
                styles.filterTabText,
                filterStatus === status && styles.filterTabTextActive,
              ]}
            >
              {status === 'all' ? 'All' : STATUS_LABELS[status as ReportStatus]}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Reports List */}
      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#000" />
        </View>
      ) : reports.length === 0 ? (
        <View style={styles.centered}>
          <Text style={styles.emptyText}>No reports found</Text>
        </View>
      ) : (
        <ScrollView style={styles.reportsList}>
          {reports.map(renderReport)}
        </ScrollView>
      )}
    </View>
  );
}

function getStatusColor(status: ReportStatus) {
  switch (status) {
    case 'pending':
      return { backgroundColor: '#fff3e0', borderColor: '#ff6b00' };
    case 'in_review':
      return { backgroundColor: '#e3f2fd', borderColor: '#0066ff' };
    case 'resolved':
      return { backgroundColor: '#e8f5e9', borderColor: '#00cc00' };
    case 'rejected':
      return { backgroundColor: '#ffebee', borderColor: '#cc0000' };
  }
}

function getPriorityColor(priority: string) {
  switch (priority) {
    case 'high':
      return { backgroundColor: '#ffebee', borderColor: '#cc0000' };
    case 'medium':
      return { backgroundColor: '#fff3e0', borderColor: '#ff6b00' };
    case 'low':
      return { backgroundColor: '#f5f5f5', borderColor: '#666' };
    default:
      return { backgroundColor: '#f5f5f5', borderColor: '#666' };
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  statsContainer: {
    padding: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#000',
    backgroundColor: '#f5f5f5',
  },
  statsTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 15,
  },
  statsGrid: {
    flexDirection: 'row',
    gap: 10,
  },
  statBox: {
    flex: 1,
    padding: 15,
    backgroundColor: '#fff',
    borderWidth: 2,
    borderColor: '#000',
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 12,
    marginTop: 5,
    color: '#666',
  },
  filterTabs: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#000',
  },
  filterTab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRightWidth: 2,
    borderRightColor: '#000',
  },
  filterTabActive: {
    backgroundColor: '#000',
  },
  filterTabText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#000',
  },
  filterTabTextActive: {
    color: '#fff',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
  },
  reportsList: {
    flex: 1,
  },
  reportCard: {
    borderBottomWidth: 2,
    borderBottomColor: '#000',
    backgroundColor: '#fff',
  },
  reportHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 15,
  },
  reportHeaderLeft: {
    flex: 1,
  },
  reportHeaderRight: {
    gap: 5,
  },
  reportIssueType: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  reportProduct: {
    fontSize: 14,
    color: '#666',
    marginBottom: 3,
  },
  reportDate: {
    fontSize: 12,
    color: '#999',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 2,
  },
  statusText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 2,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  reportDetails: {
    padding: 15,
    paddingTop: 0,
    borderTopWidth: 2,
    borderTopColor: '#000',
  },
  detailSection: {
    marginBottom: 15,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#666',
  },
  detailValue: {
    fontSize: 14,
  },
  adminActions: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 2,
    borderTopColor: '#000',
  },
  adminLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  adminInput: {
    borderWidth: 2,
    borderColor: '#000',
    padding: 10,
    fontSize: 14,
    marginBottom: 15,
    backgroundColor: '#fff',
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#000',
  },
  reviewButton: {
    backgroundColor: '#0066ff',
  },
  resolveButton: {
    backgroundColor: '#00cc00',
  },
  rejectButton: {
    backgroundColor: '#cc0000',
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  resolvedSection: {
    marginTop: 15,
    paddingTop: 15,
    borderTopWidth: 2,
    borderTopColor: '#000',
  },
});
